
import { Peers_Handler } from "../Peers.js";
import { Logger } from "../Logger.js";
import { ConferenceHealth } from "./ConferenceHealth.js";

/**
 * jaas example here: 
 * https://github.com/saghul/sandbox-html/blob/main/jaas-ljm/example.js
 */

export const jitsiApi = {

    isActive: false,

    jitsiConnection: null,
    jitsiConference: null,

    // -- settle handles for a join() in flight. The conference is created at page load but only
    // -- joined on the user's Join click, so CONFERENCE_JOINED (and any connection failure that
    // -- lands while we wait for it) has to resolve a promise that didn't exist at setup time.
    _join_deferred: null,

    createLocalTracksFromMediaStreams(tracksInfo){
        return JitsiMeetJS.createLocalTracksFromMediaStreams(tracksInfo);
    },

    buildOptions(tenant, room) {
            
        return {    
            
            hosts: {
                domain: `8x8.vc`,
                muc: `conference.${tenant}.8x8.vc`,
                focus: `focus.8x8.vc`
            },
            
            serviceUrl: `wss://8x8.vc/${tenant}/xmpp-websocket?room=${room}`,   //?release=release-3792
            websocketKeepAliveUrl: `https://8x8.vc/${tenant}/_unlock?room=${room}`,
    
            constraints: {
                video: {
                    framerate: {ideal: 30, max: 30, min: 15},
                    width: {max: 640}, 
                    height: {max: 360},
                    aspectRatio: 1.777777778,
                }
            },

            channelLastN: 9, //simultaneous number of videos that get sent from video bridge. 
            stereo: false,
            //disableNS: true,
            enableLayerSuspension: true,
            disableSimulcast: true,

            p2p: {
                enabled: false
            },
    
            logging: {

                defaultLogLevel: 'warn',
                'modules/RTC/TraceablePeerConnection.js': 'warn',
                'modules/statistics/CallStats.js': 'warn',
                'modules/xmpp/strophe.util.js': 'warn'
            },
    
            // End marker, disregard1
            __end: true
            
        };
    },

    // -- Connect but don't join. ****Tracks must be added before join****
    // -- click. See https://github.com/jitsi/ljm-jaas-example/blob/master/example.js
    connect(channel_name, writeRemoteEvent){

        console.assert(channel_name);
        console.assert(writeRemoteEvent);

        return new Promise(async (resolve, reject) => {

            try {

	            //reject({status: "error", info: "Test Conference Error"})
	
	            let token = await fetch('https://mcttj07x52.execute-api.us-east-2.amazonaws.com/default/JitsiJWT_js').then(response => response.json())
	            let credentials = {tenant: 'vpaas-magic-cookie-93bb45ae36294f9d8f62f268db014731', token: token.body, room: channel_name};
	            const options = this.buildOptions(credentials.tenant, credentials.room);
	
	            JitsiMeetJS.init(options);
	
	            JitsiMeetJS.setLogLevel(options.logging.defaultLogLevel);
	            for (const [loggerId, level] of Object.entries(options.logging)) {
	                if (loggerId !== 'defaultLogLevel') {
	                    JitsiMeetJS.setLogLevelById(level, loggerId);
	                }
            }

            this.jitsiConnection = new JitsiMeetJS.JitsiConnection(null, credentials.token, options);

            // -- before any listener below: a resume can start the moment the socket is up, and
            // -- this is the only place that sees it (see ConferenceHealth)
            ConferenceHealth.watchConnection(this.jitsiConnection);

            this.jitsiConnection.addEventListener(

                JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, () => {

                    // -- MUST be idempotent: lib-jitsi-meet emits CONNECTION_ESTABLISHED on EVERY
                    // -- xmpp CONNECTED, including a successful XEP-0198 websocket resume (stream
                    // -- resume is on by default -- we pass no enableWebsocketResume). Re-running
                    // -- initJitsiConference then throws "A conference with the same name has
                    // -- already been created!" from inside strophe's status callback, which
                    // -- unwinds the library BEFORE it emits its own CONN_STATUS_CHANGED(CONNECTED)
                    // -- -- and that emit is what drives its post-resume re-sync: ChatRoom
                    // -- re-sends the presence dropped while the socket was down (sendPresence is
                    // -- a silent no-op while disconnected) and JingleSessionPC flushes any
                    // -- cached SSRC update. Suppressing it leaves remote peers with a stale
                    // -- mute/presence view of us -- e.g. a camera mute during the resume window
                    // -- never reaches them, so their tile of us freezes on its last frame.
                    // -- (jitsi-meet removes its own listener on first fire; we guard instead so
                    // -- a genuine re-connect after teardownConference still builds a conference.)
                    if (this.jitsiConference){
                        Logger.log("Conference", "info", "Jitsi Connection Resumed", {ljm_version: JitsiMeetJS.version, is_active: this.isActive, ...ConferenceHealth.takeResumeStats()});
                        // -- settle rather than fall off the end: if this listener belongs to a
                        // -- SECOND, overlapping connect() (page-load retry still in flight when
                        // -- the Join click calls ensureConnected), its promise would otherwise
                        // -- never resolve and the Join button would spin forever. A conference
                        // -- exists and is live, which is exactly what that caller asked for.
                        // -- A no-op on the resume path, where this promise settled long ago.
                        resolve({status: "ok", info: "ok"});
                        return;
                    }

                    // -- ljm_version: the 8x8-hosted library is a rolling release (a commit hash,
                    // -- e.g. "ddc6ad7246") that can change under us without a deploy -- stamping it
                    // -- here lets forensics correlate behavior changes with library rolls
                    Logger.log("Conference", "info", "Jitsi Connection Established", {ljm_version: JitsiMeetJS.version});

                    this.jitsiConference = this.jitsiConnection.initJitsiConference(credentials.room, options);
                    ConferenceHealth.watchConference(this.jitsiConference);

                    // -- Self User Joins -- //
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.CONFERENCE_JOINED, () => {

                            const jid = this.jitsiConference.myUserId();
                            if (!jid){
                                const error = `Self joined conference w/o jid: ${jid}`;
                                this._settleJoin({status: "error", info: error, jid: jid}, true);
                                return;
                            }

                            this.isActive = true;
                            Logger.log("Conference", "info", "Conference Joined", {jid: jid});
                            this._settleJoin({status: "ok", info: "ok", jid: jid});
                        }
                    );

                    // -- Self Fails To Join -- //
                    // -- the join is now behind the Join button, so an unjoinable conference has to
                    // -- settle rather than leave the button spinning. lib-jitsi-meet does not retry
                    // -- these itself (the app is expected to), which is what the Retry state is for
                    // -- -- so don't tear down here, the conference must survive to be re-joined.
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.CONFERENCE_FAILED, (error, ...params) => {
                            Logger.log("Conference", "error", "Conference Failed", {error: error, params: String(params), ice_restarts: ConferenceHealth.iceRestarts(this.jitsiConference)});
                            this._settleJoin({status: "error", info: error ?? "Conference failed"}, true);
                        }
                    );

                    // -- Remote User Joins -- //
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.USER_JOINED, (jid, user) => {
                            writeRemoteEvent({route: "user-join", data: {jid: jid, user: user}});
                        }
                    );

                    // -- Remote or Local Tracks Added -- //
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.TRACK_ADDED, track => {
                            if (!track.isLocal()){
                                const jid = track.getParticipantId();
                                Logger.log("Conference", "info", "Remote Track Added", {jid: jid, kind: track.getType(), muted: track.isMuted()});
                                writeRemoteEvent({route: "add-track", data: {jid: jid, track: track}});
                            }
                        }
	                );

                    // -- backstop for the pl_id property parsing later than the presence that
                    // -- fired USER_JOINED (varies across the rolling LJM releases); re-enters the
                    // -- same user-join route, where setUser picks the property up idempotently
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.PARTICIPANT_PROPERTY_CHANGED, (user, key, old_value, new_value) => {
                            if (key !== "pl_id") return;
                            writeRemoteEvent({route: "user-join", data: {jid: user.getId?.() ?? user._id, user: user}});
                        }
                    );
                    
                
                    this.jitsiConference.on(
                        
                        // --- This is currently getting called twice per track for some reason
                        JitsiMeetJS.events.conference.TRACK_REMOVED, (track) => {

                                const jid = track.isLocal() ? this.jitsiConference?.myUserId() : track.getParticipantId();
                                if (!jid) return; //we are on the way out
                                console.log("[PL-Conference] Track Removed. Is Local: ", track.isLocal(), jid);
                                if (!track.isLocal())
                                    Logger.log("Conference", "info", "Remote Track Removed", {jid: jid, kind: track.getType()});
	                            writeRemoteEvent({route: "remove-track", data: {jid: jid, track: track}});
	                        }
                    );

                    this.jitsiConference.on(
	                        JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, (track) => {
	                            if (!track.isLocal()){
                                    const jid = track.getParticipantId();
	                                writeRemoteEvent({route: "track-mute", data: {jid: jid, track: track}});
	                            }
	                        }
	                    );

                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.LAST_N_ENDPOINTS_CHANGED, (entering, leaving) => {
                            //console.log('[**LAST N ENDPOINTS CHANGED: (entering, levaing) **]', entering, leaving)
                        }
                    );

                    
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.NO_AUDIO_INPUT, () => {
                            console.error('#### No Audio Input');
                        }
                    );

                    this.jitsiConference.on(                                               
                        JitsiMeetJS.events.conference.DISPLAY_NAME_CHANGED, (jid, _name) => {
                            
                        }
                    );


                    this.jitsiConference.on(

                        // -- we dont actively use this because it tends to queue messages, but is here in case the primary message sender method (below) should fail 
                        JitsiMeetJS.events.conference.MESSAGE_RECEIVED, (jid, text, ts) => {

                            let msg = JSON.parse(text);
                            let localJid = this.jitsiConference.myUserId();
                            if (msg.from == localJid)
                                return;

                            Peers_Handler.set_chat_msg(msg)
                    });

                    
                    this.jitsiConference.on(

                        JitsiMeetJS.events.conference.ENDPOINT_MESSAGE_RECEIVED, (user, msg) => {

                            if (msg.from === this.jitsiConference.myUserId())
                                return;

                            Peers_Handler.set_chat_msg(msg);
                    });


                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.CONFERENCE_LEFT, (jid, user) => {
                            Logger.log("Conference", "warn", "Conference Left", {jid: jid});
                            this.teardownConference("conference-left");
                        }
                    );

                    //When other users leave the conference
                    this.jitsiConference.on(
                        JitsiMeetJS.events.conference.USER_LEFT, (jid, user) => {
                            console.log("[PL-Conference] remote user left with jid: ", jid);
                        }
                    );
                    
                    // -- no conference join until user clicks join
                    resolve({status: "ok", info: "ok"});
                }
            );

            this.jitsiConnection.addEventListener(
                // -- (err_type, message, credentials, details) -- the third argument is
                // -- deliberately not destructured, it carries the JWT. err_type separates the
                // -- give-up after the resume budget ("connection-dropped-error") from a shard
                // -- change, a 5xx, and an auth failure, which today all log identically.
                JitsiMeetJS.events.connection.CONNECTION_FAILED, (err_type, message, _credentials, details) => {
                        console.log('*** CONNECTION ERROR ***')
                        Logger.log("Conference", "error", "Jitsi Connection Failed", {
                            error_type: err_type,
                            message: message,
                            details: details ? String(JSON.stringify(details)).slice(0, 200) : undefined,
                            ...ConferenceHealth.takeResumeStats(),
                        });
                        // -- a failure that lands while the user is mid-Join has to settle that
                        // -- promise too, or the Join button spins forever
                        this._settleJoin({status: "error", info: "Jitsi connection failed"}, true);
                        this.teardownConference("connection-failed");
                        // -- pre-connect this settles the hang (no-op once resolved)
                        reject({status: "error", info: "Jitsi connection failed"});
                }
            );

            this.jitsiConnection.addEventListener(
                // -- only ever fires for a disconnect WE asked for: xmpp.js emits this instead
                // -- of CONNECTION_FAILED solely when _disconnectInProgress is set. A real drop
                // -- always arrives as CONNECTION_FAILED above.
                JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, () => {
                    console.log('*** DISCONNECT ***')
                    Logger.log("Conference", "warn", "Jitsi Connection Disconnected");
                    this._settleJoin({status: "error", info: "Jitsi connection disconnected"}, true);
                    this.teardownConference("connection-disconnected");
                }
            );

            this.jitsiConnection.connect();

            }
            catch (e){
                const info = e instanceof Error ? e.message : String(e);
                Logger.log("Conference", "error", "Jitsi Setup Failed", {error: info});
                reject({status: "error", info: info});
            }

        });

    },


    // -- Join click: enter the conference we built at connect() time. Local tracks must already
    // -- be on it (Conference.addLocalTracks -> addTrack) so they ride out with the initial
    // -- presence/offer instead of forcing an immediate renegotiation.
    join(){

        return new Promise((resolve, reject) => {

            if (!this.jitsiConference){
                reject({status: "error", info: "No jitsi conference (not connected)"});
                return;
            }

            // -- a settings round-trip lands back on the Join button; don't re-join
            if (this.isActive){
                resolve({status: "ok", info: "ok", jid: this.jitsiConference.myUserId()});
                return;
            }

            this._join_deferred = {resolve, reject};

            try {
                // -- ride our pl-ws id out with the join presence: remote peers bind our tile
                // -- from it (USER_JOINED, or the property-changed backstop when it parses late).
                // -- The Join gate (core_init_ok) guarantees the real id exists by now.
                const pl_id = Peers_Handler.self_peer?.id;
                if (pl_id && pl_id !== "self")
                    this.jitsiConference.setLocalParticipantProperty("pl_id", pl_id);

                this.jitsiConference.join();
                // this.jitsiConference.setSenderVideoConstraint(180);
                // this.jitsiConference.setReceiverVideoConstraint(180);
            }
            catch (e){
                const info = e instanceof Error ? e.message : String(e);
                Logger.log("Conference", "error", "Conference Join Failed", {error: info});
                this._settleJoin({status: "error", info: info}, true);
            }
        });
    },


    // -- settles a join() in flight exactly once; a no-op when nothing is waiting (e.g. a
    // -- disconnect long after we joined, or a CONFERENCE_JOINED we never asked for)
    _settleJoin(result, is_error = false){

        const deferred = this._join_deferred;
        this._join_deferred = null;

        if (!deferred) return;

        is_error ? deferred.reject(result) : deferred.resolve(result);
    },

    async cleanupLocal(){

        const tracks = this.jitsiConference?.getLocalTracks() ?? [];

        // -- dispose() removes the track from the conference first, so it needs the conference
        // -- still standing; allSettled because one kind failing must not skip the other
        await Promise.allSettled(tracks.map(t => t.dispose()));

        this.teardownConference("cleanup");
    },


    /*
     Release the conference and connection objects WITHOUT touching the capture devices -- what a
     dropped socket calls for. Nothing about a network outage justifies stopping the user's camera
     and microphone, and doing so is what used to leave the local tile dark and every subsequent
     publish sending an ended track after any outage longer than lib-jitsi-meet's ~40s resume
     budget. leave() only calls onLocalTrackRemoved on the local tracks; it does not stop them.

     The JitsiLocalTrack wrappers are orphaned rather than disposed, so each keeps one RTCUtils
     DEVICE_LIST_WILL_CHANGE listener alive until GC. That handler only writes _realDeviceId /
     _trackEnded on the dead wrapper itself, so an orphan is inert -- a bounded leak, and a far
     better trade than stopping the devices. A rejoin that publishes clones would remove even
     that; see the conference-reconnect work.

     Safe to call twice: the drop path can arrive through more than one event, and our own
     disconnect() re-enters here via CONNECTION_DISCONNECTED.
    */
    teardownConference(reason){

        const was_active = this.isActive;
        const conference = this.jitsiConference;
        const connection = this.jitsiConnection;

        this.isActive = false;
        this.jitsiConference = null;
        this.jitsiConnection = null;

        ConferenceHealth.stop();

        // -- the conference exists from page load, so this can run on one that was never joined
        // -- (a pre-join disconnect, or a tab closed on the start panel); leaving an unjoined
        // -- conference throws in lib-jitsi-meet

        if (conference && was_active) {
            // -- leave() releases the peer connections, the bridge channel and the stats
            // -- reporters. It is async and RETHROWS the ChatRoom leave error -- the 5s
            // -- MUC_LEFT timeout, which is exactly what a dead socket produces -- so the
            // -- rejection has to be caught here or every drop lands in the global
            // -- unhandledrejection logger. rtc.destroy() runs regardless of that throw.
            try { Promise.resolve(conference.leave()).catch(e => console.warn("[PL-Conference] Leave failed", e)); }
            catch (e){ console.warn("[PL-Conference] Leave threw", e); }
        }

        if (connection)
            connection.disconnect();

        console.log("[PL-Conference] Conference torn down:", reason);
    },

}


// Enable Callstats (note, none of this is secret, despite its name)
// callStatsID: '706724306',
// callStatsSecret: 'f+TKWryzPOyX:dNR8PMw42WJwM3YM1XkJUjPOLY0M40wz+0D4mZud8mQ=',
// confID: `https://8x8.vc/${tenant}/${room}`,
// siteID: tenant,
// applicationName: 'Postlink',

// // Misc
// deploymentInfo: {userRegion: 'default'},
